import React, { useMemo, useState, useEffect, useRef } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { Checkbox, RadioGroup } from "@progress/kendo-react-inputs";
import { Label } from "@progress/kendo-react-labels";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { Form } from "@progress/kendo-react-form";
import { getReportTemplates, updateBillSettings } from "api";
import { toast } from "react-toastify";
import SubmitButton from "components/common/SubmitButton";
import { Tile } from "components/UIComponents/Tile";
import AddressInputComponent from "components/common/AddressInputComponent";
import {
  mergeAddressForDisplay,
  normalizeAddressForApi,
} from "pages/Bills/normalizeAddress";
import { isNonEmptyBillResourceId } from "pages/Bills/billReportResourceId";

const BILL_TEMPLATE_TYPE = "BillingTemplate";
const GUID_EMPTY = "00000000-0000-0000-0000-000000000000";

const ADDRESS_TYPES = [
  { label: "Correspondence", value: "Correspondence" },
  { label: "Statement", value: "Statement" },
  { label: "Billing", value: "Billing" },
  { label: "Other", value: "Other" },
];

const BILL_ADDRESS_TYPE = "billAddress";

const toAddressFormValues = (address, prefix) => ({
  [`${prefix}line1`]: mergeAddressForDisplay(address),
  [`${prefix}town`]: address?.town ?? "",
  [`${prefix}state`]: address?.state ?? "",
  [`${prefix}postcode`]: address?.postcode ?? "",
  [`${prefix}country`]: address?.country ?? "",
});

const toOtherAddressPayload = (formData, prefix) =>
  normalizeAddressForApi({
    line1: formData?.[`${prefix}line1`] ?? "",
    line2: formData?.[`${prefix}line2`] ?? "",
    town: formData?.[`${prefix}town`] ?? "",
    state: formData?.[`${prefix}state`] ?? "",
    postcode: formData?.[`${prefix}postcode`] ?? "",
    countryCode: null,
    country: formData?.[`${prefix}country`] ?? "",
  });

const BillSettingsDialog = ({
  open,
  onClose,
  matterId,
  billSettings,
  selectedReportResourceId,
  fallbackReportResourceId,
  selectedAddressType,
  selectedOtherAddress,
  onSaved,
}) => {
  const [billTemplates, setBillTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressType, setAddressType] = useState("Billing");
  const [reportLayout, setReportLayout] = useState(null);
  const [addressAsDefault, setAddressAsDefault] = useState(false);
  const [reportLayoutAsDefault, setReportLayoutAsDefault] = useState(false);
  const [otherAddressDraft, setOtherAddressDraft] = useState({});
  const formSubmitRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const initialType =
      selectedAddressType ?? billSettings?.defaultAddressType ?? "Billing";
    setAddressType(initialType);
    setAddressAsDefault(initialType === billSettings?.defaultAddressType);
    setOtherAddressDraft(
      toAddressFormValues(
        selectedOtherAddress ?? billSettings?.otherAddress,
        BILL_ADDRESS_TYPE,
      ),
    );
  }, [
    open,
    billSettings?.defaultAddressType,
    billSettings?.otherAddress,
    selectedAddressType,
    selectedOtherAddress,
  ]);

  const addressInitialValues = useMemo(() => {
    const readOnlySource =
      addressType === "Correspondence"
        ? billSettings?.correspondenceAddress
        : addressType === "Statement"
          ? billSettings?.statementAddress
          : addressType === "Billing"
            ? billSettings?.billingAddress
            : null;

    if (addressType === "Other") return otherAddressDraft;

    return toAddressFormValues(readOnlySource, BILL_ADDRESS_TYPE);
  }, [
    addressType,
    billSettings?.correspondenceAddress,
    billSettings?.statementAddress,
    billSettings?.billingAddress,
    otherAddressDraft,
  ]);

  useEffect(() => {
    if (open) {
      loadActiveBillTemplates();
    }
  }, [open]);

  useEffect(() => {
    if (!open || !billTemplates?.length) return;
    const defaultId =
      (isNonEmptyBillResourceId(selectedReportResourceId)
        ? selectedReportResourceId
        : null) ??
      (isNonEmptyBillResourceId(fallbackReportResourceId)
        ? fallbackReportResourceId
        : null);
    const validId = defaultId && defaultId !== GUID_EMPTY ? defaultId : null;
    const template = validId
      ? billTemplates.find((t) => t.reportResourceId === validId)
      : null;
    if (validId) {
      setReportLayout(template || null);
    } else {
      setReportLayout(null);
    }
    setReportLayoutAsDefault(
      !!(
        validId &&
        billSettings?.defaultBillResourceId &&
        billSettings.defaultBillResourceId !== GUID_EMPTY &&
        validId === billSettings.defaultBillResourceId
      ),
    );
  }, [
    open,
    billSettings?.defaultBillResourceId,
    selectedReportResourceId,
    fallbackReportResourceId,
    billTemplates,
  ]);

  const loadActiveBillTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const response = await getReportTemplates(BILL_TEMPLATE_TYPE);
      const all = response?.data?.data || [];
      const active = all.filter((t) => t.active === true);
      setBillTemplates(active);
    } catch (err) {
      setBillTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleSave = async (formData) => {
    if (!matterId) {
      toast.error("No matter selected");
      return;
    }
    try {
      setIsSubmitting(true);
      const value = reportLayout?.reportResourceId ?? GUID_EMPTY;
      const otherAddressPayload = toOtherAddressPayload(
        formData,
        BILL_ADDRESS_TYPE,
      );
      const updates = {};
      const patchPayload = {};

      if (reportLayoutAsDefault) {
        patchPayload.defaultBillResourceId = value;
        updates.defaultBillResourceId = value;
      } else {
        updates.matterBillResourceId = value;
      }

      if (addressAsDefault) {
        patchPayload.defaultAddressType = addressType;
        if (addressType === "Other")
          patchPayload.otherAddress = otherAddressPayload;
        updates.defaultAddressType = addressType;
        if (addressType === "Other") updates.otherAddress = otherAddressPayload;
      } else {
        updates.selectedAddressType = addressType;
        if (addressType === "Other") updates.otherAddress = otherAddressPayload;
      }

      if (Object.keys(patchPayload).length) {
        await updateBillSettings(matterId, patchPayload);
      }

      onSaved?.(updates);
      onClose?.();
    } catch (err) {
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  const formKey = `${matterId || "no-matter"}-${addressType}-${billSettings?.defaultAddressType ?? "na"}`;
  const readOnlyAddress = addressType !== "Other";

  return (
    <Dialog title="Bill Settings" onClose={onClose} className="dialog-sm">
      <Form
        key={formKey}
        ignoreModified
        initialValues={addressInitialValues}
        onSubmit={handleSave}
        render={(formRenderProps) => {
          formSubmitRef.current = formRenderProps.onSubmit;
          return (
            <div className="p-3 px-4">
              <Tile
                heading="Bill Address"
                className="px-4 pb-4 mb-3 overflow-auto"
              >
                <RadioGroup
                  data={ADDRESS_TYPES}
                  value={addressType}
                  onChange={(e) => {
                    const next = e.value;
                    setAddressType(next);
                    setAddressAsDefault(next === billSettings?.defaultAddressType);
                  }}
                  layout="horizontal"
                  className="d-flex flex-wrap radio gap-2 my-2"
                />
                <AddressInputComponent
                  name="Address"
                  type={BILL_ADDRESS_TYPE}
                  width="275px"
                  readOnly={readOnlyAddress}
                  setAddress={(addr) => {
                    if (addressType === "Other")
                      setOtherAddressDraft(addr || {});
                  }}
                  style={{ width: "610px" }}
                />
                <div
                  className="fw-semibold mt-3"
                  title={
                    addressType === billSettings?.defaultAddressType
                      ? "This address is already the matter default"
                      : undefined
                  }
                >
                  <Checkbox
                    label="Set as Matter Default"
                    checked={addressAsDefault}
                    onChange={(e) => setAddressAsDefault(e.value)}
                    disabled={addressType === billSettings?.defaultAddressType}
                  />
                </div>
              </Tile>

              <Tile className="px-4 py-4 overflow-auto">
                <Label className="label mb-2 w-100 fw-semibold">
                  Report Layout
                </Label>
                <ComboBox
                  data={billTemplates}
                  value={reportLayout}
                  onChange={(e) => {
                    const next = e.value;
                    setReportLayout(next);
                    const nextId = next?.reportResourceId;
                    const isDefault = !!(
                      nextId &&
                      billSettings?.defaultBillResourceId &&
                      billSettings.defaultBillResourceId !== GUID_EMPTY &&
                      nextId === billSettings.defaultBillResourceId
                    );
                    setReportLayoutAsDefault(isDefault);
                  }}
                  textField="templateName"
                  dataItemKey="reportResourceId"
                  placeholder={loadingTemplates ? "Loading..." : "Select Type"}
                  disabled={loadingTemplates}
                  style={{ width: "100%", height: "36px" }}
                  suggest={true}
                  filterable={true}
                />
                <div
                  className="fw-semibold mt-3"
                  title={
                    reportLayout?.reportResourceId ===
                    billSettings?.defaultBillResourceId
                      ? "This report is already the matter default"
                      : undefined
                  }
                >
                  <Checkbox
                    label="Set as Matter Default"
                    checked={reportLayoutAsDefault}
                    onChange={(e) => setReportLayoutAsDefault(e.value)}
                    disabled={
                      !!(
                        reportLayout?.reportResourceId &&
                        billSettings?.defaultBillResourceId &&
                        billSettings.defaultBillResourceId !== GUID_EMPTY &&
                        reportLayout.reportResourceId ===
                          billSettings.defaultBillResourceId
                      )
                    }
                  />
                </div>
              </Tile>
            </div>
          );
        }}
      />
      <DialogActionsBar>
        <div className="dialog-footer w-100 d-flex justify-content-end gap-2 px-4">
          <Button className="common-btn bgBtnSecondary" onClick={onClose}>
            Cancel
          </Button>
          <SubmitButton
            onClick={() => formSubmitRef.current?.()}
            isLoading={isSubmitting}
            disabled={isSubmitting}
          >
            Save
          </SubmitButton>
        </div>
      </DialogActionsBar>
    </Dialog>
  );
};

export default BillSettingsDialog;
